import React, { useState, useEffect, useLayoutEffect, useRef } from "react";

const navItems = [
  { name: "Inicio", path: "#hero" },
  { name: "Sobre Nosotros", path: "#about" },
  { name: "Servicios", path: "#services" },
  { name: "Proyectos", path: "#proyects" },
  { name: "Preguntas Frecuentes", path: "#questions" },
  { name: "Contáctanos", path: "#footer" },
];


const Navbar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("#hero");
  const [scrolled, setScrolled] = useState(false);

  const navRowRef = useRef<HTMLDivElement | null>(null);
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const moveIndicator = () => {
    const row = navRowRef.current;
    const indicator = indicatorRef.current;
    const activeBtn = itemRefs.current[activeSection];

    if (!row || !indicator || !activeBtn) return;

    const rowRect = row.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();

    const left = btnRect.left - rowRect.left;
    const width = btnRect.width;

    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
    indicator.style.opacity = "1";
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });

    const sections = navItems.map((item) => document.querySelector(item.path));
    const observerOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: "-50% 0px -50% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(`#${entry.target.id}`);
      });
    }, observerOptions);

    sections.forEach((section) => section && observer.observe(section));

    const detectActiveSection = () => {
      const scrollMiddle = window.innerHeight / 2 + window.scrollY;
      let currentSection = "#hero";

      for (const section of sections) {
        if (!section) continue;
        const rect = section.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const bottom = top + rect.height;

        if (scrollMiddle >= top && scrollMiddle < bottom) {
          currentSection = `#${section.id}`;
          break;
        }
      }
      setActiveSection(currentSection);
    };

    detectActiveSection();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      sections.forEach((section) => section && observer.unobserve(section));
    };
  }, []);

  const handleLinkClick = (path: string) => {
    const section = document.querySelector(path);
    if (section) section.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  useEffect(() => {
    if (!menuOpen) return;

    const sections = navItems.map((item) => document.querySelector(item.path));
    const scrollMiddle = window.innerHeight / 2 + window.scrollY;
    let currentSection = "#hero";

    for (const section of sections) {
      if (!section) continue;
      const rect = section.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const bottom = top + rect.height;

      if (scrollMiddle >= top && scrollMiddle < bottom) {
        currentSection = `#${section.id}`;
        break;
      }
    }
    setActiveSection(currentSection);
  }, [menuOpen]);

  useLayoutEffect(() => {
    moveIndicator();
  }, [activeSection]);

  useEffect(() => {
    const onResize = () => moveIndicator();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [activeSection]);

  return (
    <nav
      className={`fixed w-full top-0 left-0 z-50 transition-all duration-300
        ${
          scrolled
            ? "bg-black/70 backdrop-blur-md shadow-lg"
            : "bg-black/30 backdrop-blur-sm"
        }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              loading="lazy"
              decoding="async"
              id="navbar-logo"
              src={"https://res.cloudinary.com/xvzxvfee/image/upload/v1788743201/logoSprintCoreFigma_nsm6bq.webp"}
              alt="SprintCore"
              className="h-8 w-auto filter brightness-110"
            />
          </div>

          <div className="hidden sm:flex items-center justify-end flex-1">
            <div
              ref={navRowRef}
              className="relative flex items-center space-x-1"
            >
              {/* Indicador */}
              <div
                ref={indicatorRef}
                aria-hidden="true"
                className="
                  absolute bottom-0 left-0 h-2 rounded-full
                  bg-gradient-to-r from-black/20 via-prin5 to-prin1
                  transition-all duration-300 ease-out
                  opacity-0
                "
                style={{
                  width: 0,
                  transform: "translateX(0px)",
                }}
              />

              {navItems.map((item) => {
                const isActive = activeSection === item.path;
                return (
                  <button
                    key={item.name}
                    ref={(el: HTMLButtonElement | null) => {
                      itemRefs.current[item.path] = el;
                    }}
                    onClick={() => handleLinkClick(item.path)}
                    className={`
                      relative px-3 lg:px-4 py-2 text-xs lg:text-sm transition-colors duration-200
                      ${
                        isActive
                          ? "text-white font-semibold"
                          : "text-white/65 font-normal hover:text-white"
                      }
                    `}
                  >
                    {item.name}
                  </button>
                );
              })}

            </div>
          </div>

          {/* Toggle móvil */}
          <div className="flex items-center space-x-2 lg:space-x-3">
            <button
              className="sm:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-all duration-200 focus:outline-none"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Menú móvil */}
      <div
        className={`
          sm:hidden transition-all duration-300 ease-in-out
          ${menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"}
        `}
      >
        <div className="bg-black/60 backdrop-blur-xl border-t border-white/10">
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleLinkClick(item.path)}
                className={`
                  block w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
                  ${
                    activeSection === item.path
                      ? "bg-white/20 text-white shadow-lg"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  }
                `}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
